/// <amd-module name="DistributedTaskUI/ServiceEndpoints/Editor/UIContributions/AzureRMEndpoint/Components/Manual/ManualSecretComponent" />
import React = require("react");
import { IdentityCredentialTypeComponent } from "./IdentityCredentialTypeComponent";
import { GenericAuthorizationComponent } from "DistributedTaskUI/ServiceEndpoints/Editor/Components/GenericEndpointComponents/GenericAuthorizationComponent";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { EndpointsEditorStore } from "DistributedTaskUI/ServiceEndpoints/Editor/Stores/EndpointsEditorStore";
import { StoreManager } from "DistributedTask/Flux/StoreManager";
import { ARMAuthenticationDetailsHeader } from "./ARMAuthenticationDetailsHeader";
import { ServiceEndpoint } from "ServiceEndpoints/Client/Contracts/ServiceEndpoint";
import { EndpointsEditorActionsCreator } from "DistributedTaskUI/ServiceEndpoints/Editor/Actions/EndpointsEditorActionsCreator";
import { ActionCreatorManager } from "DistributedTask/Flux/ActionCreatorManager";
import { ActionsCreatorInstanceIds, TestConnectionDataSource } from "DistributedTaskUI/ServiceEndpoints/Editor/Constants";
import { EndpointNameInput } from "../Common/EndpointNameInput";
import { EndpointDescriptionInput } from "../Common/EndpointDescriptionInput";
import { InputValidationStore } from "DistributedTaskUI/ServiceEndpoints/Editor/Stores/InputValidationStore";
import { PipelinePermissionsCheckbox } from "../Common/PipelinePermissionsCheckbox";
import { EndpointErrorMessageWidget } from "../Common/EndpointErrorMessageWidget";
import { ARMEndpointFooterComponent } from "./ARMEndpointFooterComponent";
import { BackButton, CancelButton } from "../Common/BackButton";
import { VerifyAndSaveComponent } from "./VerifyAndSaveComponent";
import { ARMEndpointVerificationComponent } from "./ARMEndpointVerificationComponent";
import { EndpointSaveProgressStore, EndpointSaveProgressStoreEvents, createEndpointSaveProgressStore, deleteEndpointSaveProgressStore } from "DistributedTaskUI/ServiceEndpoints/Editor/Stores/EndpointSaveProgressStore";
import { NavigationActionsCreator } from "DistributedTaskUI/ServiceEndpoints/Editor/Actions/NavigationActionsCreator";


interface IManualSecretComponentState {
    isEndpointValid: boolean;
    hasInvalidInputs: boolean;
}

export class ManualSecretComponent extends VssComponent<{}, IManualSecretComponentState>
{
    private _invalidInputsCount = 0;

    private readonly _endpointEditorStore: EndpointsEditorStore;
    private readonly _endpointEditorActionCreator: EndpointsEditorActionsCreator;
    private readonly _validationStore: InputValidationStore;
    private readonly _validationScopeId = ManualSecretComponent.name;
    private readonly _navigationActionsCreator: NavigationActionsCreator;
    private readonly _endpointSaveProgressStore: EndpointSaveProgressStore;

    constructor(context: IVssComponentContext) {
        super(context);

        this._endpointEditorStore = StoreManager.GetStore<EndpointsEditorStore>(EndpointsEditorStore, ActionsCreatorInstanceIds.EndpointsEditorStoreInstanceId);
        this._endpointEditorActionCreator = ActionCreatorManager.GetActionCreator<EndpointsEditorActionsCreator>(EndpointsEditorActionsCreator, ActionsCreatorInstanceIds.EndpointsEditorActionsCreatorInstanceId);
        this._navigationActionsCreator =  ActionCreatorManager.GetActionCreator<NavigationActionsCreator>(NavigationActionsCreator, ActionsCreatorInstanceIds.NavigationActionsCreatorInstanceId);

        this._validationStore = StoreManager.GetStore<InputValidationStore>(InputValidationStore, this._validationScopeId);
        this._validationStore.addChangedListener(this._onValidationStoreChanged);

        this._endpointSaveProgressStore = createEndpointSaveProgressStore();
        this._endpointSaveProgressStore.addListener(EndpointSaveProgressStoreEvents.Succeeded, this._onEndpointSaveSuccess);

        this.state = {
            isEndpointValid: false,
            hasInvalidInputs: false
        };
    }

    public componentWillUnmount(): void {
        this._validationStore.removeChangedListener(this._onValidationStoreChanged);
        StoreManager.DeleteStore<InputValidationStore>(InputValidationStore, this._validationScopeId);

        this._endpointSaveProgressStore.removeListener(EndpointSaveProgressStoreEvents.Succeeded, this._onEndpointSaveSuccess);
        deleteEndpointSaveProgressStore(this._endpointSaveProgressStore);
    }

    public render(): JSX.Element {
        const endpoint = this._endpointEditorStore.getEndpointClone();
        const endpointType = this._endpointEditorStore.getState().serviceEndpointType;
        const isUpdateFlow = !!endpoint.id;

        return (<div className="endpoint-editor-panel-content">
            {!isUpdateFlow && <IdentityCredentialTypeComponent />}

            <GenericAuthorizationComponent
                disableAllPipelinesSetting={false}
                serviceEndpointType={endpointType}
                draftEndpoint={endpoint}
                isUpdateForm={isUpdateFlow}
                updateDraftEndpointState={this._updateEndpointState}
                updateInvalidInputsCount={this._updateInvalidInputsCount}
                renderAuthenticationHeader={() => <ARMAuthenticationDetailsHeader />}
            />

            <ARMEndpointVerificationComponent disabled={this.state.hasInvalidInputs}/>

            <EndpointNameInput validationScopeId={this._validationScopeId} />
            <EndpointDescriptionInput validationScopeId={this._validationScopeId} />
            <PipelinePermissionsCheckbox />
            <EndpointErrorMessageWidget />

            <ARMEndpointFooterComponent renderButtons={this._renderButtonSection} />

        </div>);
    }

    private _updateEndpointState = (endpoint: ServiceEndpoint): void => {
        this._endpointEditorActionCreator.setServiceEndpoint(endpoint);
    }

    private _updateInvalidInputsCount = (invalidInputCount: number): void => {
        this._invalidInputsCount += invalidInputCount;
        this.setState({
            hasInvalidInputs: this._invalidInputsCount > 0
        });
    }

    private _onValidationStoreChanged = (): void => {
        const state = this._validationStore.getState();
        this.setState({
            isEndpointValid: state.isValid
        });
    }

    private _renderButtonSection = (): JSX.Element => {
        const endpoint = this._endpointEditorStore.getEndpointClone();
        return (<React.Fragment>
            {endpoint.id ? <CancelButton /> : <BackButton />}
            {this.renderSaveButton()}
        </React.Fragment>);
    }

    private renderSaveButton(): JSX.Element {
        const disableSave = this.state.hasInvalidInputs || !this.state.isEndpointValid;
        return (<VerifyAndSaveComponent
            disabled={disableSave}
            handleSaveClick={this.saveEndpoint}
            handleVerifyAndSave={this.verifyAndSaveEndpoint}
        />);
    }

    private verifyAndSaveEndpoint = (): void => {
        const isOpenToAllPipelines = this._endpointEditorStore.isOpenToAllPipelines();
        const endpoint = this._endpointEditorStore.getEndpointClone();
        this._endpointEditorActionCreator.verifyAndSaveServiceEndpoint(endpoint, TestConnectionDataSource, isOpenToAllPipelines, !!endpoint.id);

    }

    private saveEndpoint = (): void => {
        const isOpenToAllPipelines = this._endpointEditorStore.isOpenToAllPipelines();
        const endpoint = this._endpointEditorStore.getEndpointClone();
        this._endpointEditorActionCreator.customSaveServiceEndpoint(endpoint, isOpenToAllPipelines, !!endpoint.id);
    }

    private _onEndpointSaveSuccess = (): void => {
        this._navigationActionsCreator.closePanel();
    }
}


